import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import NavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import generalDataService from '../../services/data/general-data-service';
import SphereLogo from '../../media/sphereFullLogo.png';

type UnsubscribeState = 'loading' | 'unsubscribed' | 'invalid';

const UnsubscribePage: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState] = useState<UnsubscribeState>('loading');

  useEffect(() => {
    let active = true;

    const run = async () => {
      try {
        const result = await generalDataService.unsubscribe(token ?? '');
        if (active) {
          setState(result.status === 'unsubscribed' ? 'unsubscribed' : 'invalid');
        }
      } catch {
        if (active) {
          setState('invalid');
        }
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [token]);

  const isLoading = state === 'loading';
  const isSuccess = state === 'unsubscribed';

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={4}
      sx={{ minHeight: '100vh', width: '100%', padding: 3 }}
    >
      <img src={SphereLogo} alt="Sphere by Kemaro" style={{ width: 'min(384px, 100%)' }} />

      <Stack spacing={2} sx={{ width: 'min(480px, 100%)' }}>
        <CustomAlert
          icon={isLoading ? <CircularProgress size={24} /> : undefined}
          severity={isLoading ? 'info' : isSuccess ? 'success' : 'error'}
          header={
            isLoading
              ? t('unsubscribe-loading')
              : isSuccess
                ? t('unsubscribe-success-title')
                : t('unsubscribe-error-title')
          }
          textMessages={
            isLoading
              ? undefined
              : isSuccess
                ? [t('unsubscribe-success-tip')]
                : [t('unsubscribe-error-message')]
          }
        />

        {!isLoading && (
          <Button variant="contained" onClick={() => navigate(NavigationRoutes.Dashboard)}>
            {t('unsubscribe-go-to-sphere')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default UnsubscribePage;
